import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import { requestErrorMessage } from "@/lib/requestError";
import { formatTimestamp } from "@/utils/datetime";
import { newestFirst } from "@/utils/sortHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

export default function RemovedAttachmentsPanel(props) {
  const { user, hasPermission, isFeatureEnabled } = useAuth();
  const module = `project_${props.kind}`;
  if (!isFeatureEnabled(module) || !hasPermission(`${module}:manage`)) return null;
  return <RecoveryList key={`${user?.id}|${user?.company_id}|${user?.role}|${props.projectId}|${props.kind}`} {...props} />;
}

function RecoveryList({ projectId, kind, onRestored, refreshKey = 0 }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const live = useRef(false);
  const generation = useRef(0);
  const lock = useRef(false);
  const base = `${API}/projects/${projectId}/${kind}`;
  const load = useCallback(async (before = "") => {
    const current = ++generation.current;
    setLoading(true); setError("");
    try {
      const response = await axios.get(`${base}/removed`, { params: { limit: 25, ...(before ? { before } : {}) } });
      if (!live.current || current !== generation.current) return;
      setItems(existing => before ? [...existing, ...response.data.items.filter(item => !existing.some(row => row.id === item.id))] : response.data.items);
      setCursor(response.data.next_cursor || null);
    } catch (failure) {
      if (live.current && current === generation.current) {
        if ([401, 403].includes(failure.response?.status)) { setItems([]); setCursor(null); }
        setError(requestErrorMessage(failure, "Removed attachments could not be loaded. Please try again."));
      }
    } finally { if (live.current && current === generation.current) setLoading(false); }
  }, [base]);
  useEffect(() => {
    live.current = true;
    const requests = generation;
    return () => { live.current = false; requests.current++; };
  }, []);
  useEffect(() => {
    if (open) load();
    const requests = generation;
    return () => { requests.current++; };
  }, [open, load, refreshKey]);
  const restore = async item => {
    if (lock.current) return;
    lock.current = true; setBusy(item.id); setError("");
    try {
      await axios.post(`${base}/${item.id}/restore`);
      if (!live.current) return;
      generation.current++;
      setLoading(false);
      setItems(existing => existing.filter(row => row.id !== item.id));
      toast.success(`${kind === "photos" ? "Photo" : "Document"} restored.`);
      await onRestored?.();
    } catch (failure) {
      if (live.current) setError(requestErrorMessage(failure, "Restoration could not be confirmed. Refresh and try again."));
    } finally { lock.current = false; if (live.current) setBusy(""); }
  };
  return <div className="removed-attachments-panel">
    <button className="table-edit-button" type="button" aria-expanded={open} onClick={() => setOpen(value => !value)}>Recently removed {kind}</button>
    {open && <section aria-label={`Recently removed ${kind}`}>
      <p>Restore attachments during their 30-day recovery window. The original file must still be available.</p>
      {loading && <p role="status">Loading removed {kind}…</p>}
      {error && <p role="alert">{error}</p>}
      <button className="table-edit-button" type="button" disabled={loading || Boolean(busy)} onClick={() => load()}>Refresh removed {kind}</button>
      {!loading && !error && !items.length && <p>No recoverable {kind}.</p>}
      <ul className="removed-attachments-list">
        {newestFirst(items, "deleted_at").map(item => <li key={item.id}>
          <div><strong>{item.original_filename}</strong>
            <p>Removed {formatTimestamp(item.deleted_at)} · {item.days_remaining} days remaining · Expires {formatTimestamp(item.purge_after)}</p>
            {!item.restorable && <p>This attachment is no longer available for recovery.</p>}
          </div>
          <button className="table-edit-button" type="button" aria-label={`Restore ${item.original_filename}`} disabled={Boolean(busy) || loading || !item.restorable} onClick={() => restore(item)}>
            {busy === item.id ? "Restoring…" : "Restore"}
          </button>
        </li>)}
      </ul>
      {cursor && <button className="table-edit-button" type="button" disabled={loading || Boolean(busy)} onClick={() => load(cursor)}>Load older removed {kind}</button>}
    </section>}
  </div>;
}
